import React, { useState } from "react";
import { Box, Button, Collapse, Typography } from "@mui/material";
import { UserExerciseDto } from "@growth-machine-llc/stridist-api-client";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles((theme) => ({
  list: {
    padding: 0,
    margin: 0,
    listStyle: "none",
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
  title: {
    textDecoration: "underline",
    color: theme.palette.primary.main,
  },
  showMoreBtn: {
    textTransform: "none",
    alignSelf: "flex-start",
    padding: 0,
  },
}));

interface Props {
  assets: any[];
  onExerciseSelect?: (exercise: UserExerciseDto) => void;
  onShowMore?: () => void;
  initialLimit?: number;
  initialExpanded?: boolean;
}

const ExerciseList: React.FC<Props> = ({
  assets = [],
  onExerciseSelect,
  initialLimit = 5,
  onShowMore,
  initialExpanded,
}) => {
  const s = useStyles();

  const [expanded, setExpanded] = useState(initialExpanded);

  const uniqueAssets = (assets ?? []).reduce(
    (acc, a) => {
      if (!acc.some((x) => x.id === a.id)) acc.push(a);
      return acc;
    },
    [] as typeof assets,
  );

  const visibleAssets = uniqueAssets.slice(0, initialLimit);
  const hiddenAssets = uniqueAssets.slice(initialLimit);

  return (
    <Box
      display={"flex"}
      flexDirection="column"
      mb={hiddenAssets.length > 0 ? 0.5 : 1}
    >
      <Box component="ul" className={s.list}>
        {visibleAssets.map((asset) => (
          <Box
            component="li"
            key={asset.id}
            className={s.listItem}
            onClick={() =>
              onExerciseSelect?.(
                new UserExerciseDto({
                  id: 0,
                  exercise: asset,
                  title: asset.title,
                }),
              )
            }
          >
            <Box className={s.dot} />
            <Box className={s.title}>{asset.title}</Box>
          </Box>
        ))}
      </Box>

      {hiddenAssets.length > 0 && (
        <Collapse in={expanded}>
          <Box component="ul" className={s.list}>
            {hiddenAssets.map((asset) => (
              <Box
                component="li"
                key={asset.id}
                className={s.listItem}
                onClick={() =>
                  onExerciseSelect?.(
                    new UserExerciseDto({
                      id: 0, // id should be 0 since we select from assets list (not user exercises)
                      exercise: asset,
                      title: asset.title,
                    }),
                  )
                }
              >
                <Box className={s.dot} />
                <Box className={s.title}>{asset.title}</Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      )}

      {hiddenAssets.length > 0 && (
        <Button
          size="small"
          className={s.showMoreBtn}
          onClick={() => {
            if (onShowMore) {
              onShowMore();
            }
            setExpanded(!expanded);
          }}
        >
          {expanded ? "Show less" : `Show ${hiddenAssets.length} more`}
        </Button>
      )}
    </Box>
  );
};

export default ExerciseList;
